// src/utils/attemptUtils.js
import { CLIMBING_STYLES } from './constants.js';

import { getAscentPriority, isFirstTimeAscent } from './ascentTypes.js';

// Replace existing getAttemptPriority function
export function getAttemptPriority(ascentType) {
  return getAscentPriority(ascentType);
}

/**
 * Sort records by date and ascent type priority
 */
export function sortAttemptsByDateAndPriority(records) {
  return [...records].sort((a, b) => {
    const dateA = new Date(a["Ascent Date"]);
    const dateB = new Date(b["Ascent Date"]);

    // If dates are different, sort by date
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA - dateB;
    }

    // If same date, prioritize flashes/onsights
    return getAttemptPriority(a["Ascent Type"]) - getAttemptPriority(b["Ascent Type"]);
  });
}

/**
 * Sort records chronologically with clean sends last on same day.
 * Used for computing attempt numbers - ensures a redpoint after
 * working on the same day counts as attempt #2.
 */
export function sortChronologicallyCleanSendsLast(records) {
  return [...records].sort((a, b) => {
    const dateA = new Date(a["Ascent Date"]).getTime();
    const dateB = new Date(b["Ascent Date"]).getTime();
    if (dateA !== dateB) {
      return dateA - dateB;
    }
    // Same day: clean sends go last
    return a.isClean ? 1 : -1;
  });
}

/**
 * Process attempts to identify first attempts and handle same-day attempts
 */
export function processAttempts(attempts) {
  // Pre-compute attemptNumber using chronological order with clean sends last
  // This ensures redpoint after working on same day = attempt #2
  const chronologicalAttempts = sortChronologicallyCleanSendsLast(attempts);
  const routeAttemptCount = new Map();
  const attemptNumberMap = new Map(); // Map Ascent ID -> attemptNumber

  chronologicalAttempts.forEach(attempt => {
    const routeId = attempt["Route ID"];
    const attemptNumber = (routeAttemptCount.get(routeId) || 0) + 1;
    routeAttemptCount.set(routeId, attemptNumber);
    attemptNumberMap.set(attempt["Ascent ID"], attemptNumber);
  });

  // Now process with priority-based sorting for other fields
  const sortedAttempts = sortAttemptsByDateAndPriority(attempts);

  // Track first attempts at each route
  const routeHistory = new Map(); // Map of 'Route ID' to first attempt info

  return sortedAttempts.map(attempt => {
    const routeId = attempt["Route ID"];
    const ascentDate = new Date(attempt["Ascent Date"]);
    const dateStr = ascentDate.toISOString().split('T')[0];
    const year = ascentDate.getFullYear();

    // Get pre-computed attempt number
    const attemptNumber = attemptNumberMap.get(attempt["Ascent ID"]);

    let isFirstAttempt = true;
    let firstAttemptYear = year;
    let isFirstOfDay = true;
    let shouldCount = true;

    if (routeHistory.has(routeId)) {
      const firstAttempt = routeHistory.get(routeId);
      isFirstAttempt = false;
      firstAttemptYear = firstAttempt.year;

      // Check if this is a same-day attempt
      if (firstAttempt.dateStr === dateStr) {
        isFirstOfDay = false;

        // If current attempt is flash/onsight and previous wasn't, replace it as the first attempt
        const currentPriority = getAttemptPriority(attempt["Ascent Type"]);
        const previousPriority = getAttemptPriority(firstAttempt.type);

        if (currentPriority < previousPriority) {
          // Update history with the better attempt
          routeHistory.set(routeId, {
            dateStr,
            type: attempt["Ascent Type"],
            year
          });
          isFirstAttempt = true; // This becomes the first attempt
          shouldCount = true;
        } else {
          shouldCount = false; // Skip this attempt as we have a better one
        }
      } else {
        shouldCount = false; // Not a first attempt, don't count it
      }
    } else {
      // First time seeing this route
      routeHistory.set(routeId, {
        dateStr,
        type: attempt["Ascent Type"],
        year
      });
    }

    return {
      ...attempt,
      isFirstAttempt,
      firstAttemptYear,
      isFirstOfDay,
      shouldCount,
      attemptNumber
    };
  });
}

/**
 * Calculate statistics for first attempts at each grade
 */
export function calculateGradeStats(attempts, climbingStyle) {
  const gradeStats = {};
  
  // Only use attempts that should be counted (first attempts)
  const validAttempts = attempts.filter(attempt => attempt.shouldCount);
  
  validAttempts.forEach(attempt => {
    const grade = climbingStyle === CLIMBING_STYLES.BOULDER ? 
      Number(attempt.vgrade) : 
      Number(attempt.ewbanks);

    if (grade === null || isNaN(grade)) return;
    
    const climbKey = attempt['Route ID'];
    if (!climbKey) return;
    
    if (!gradeStats[grade]) {
      gradeStats[grade] = {
        grade,
        totalRoutes: new Set(),
        flashedRoutes: new Set(),
        firstAttempts: new Set()
      };
    }
    
    gradeStats[grade].totalRoutes.add(climbKey);
    gradeStats[grade].firstAttempts.add(climbKey);
    
if (isFirstTimeAscent(attempt['Ascent Type'])) {
  gradeStats[grade].flashedRoutes.add(climbKey);
}
  });
  
  return gradeStats;
}